import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AIWorker } from '../types';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface Props {
  worker: AIWorker | null;
  onClose: () => void;
}

const typeIcons: Record<string, string> = {
  info: 'ℹ️',
  success: '✅',
  error: '❌',
  warning: '⚠️',
};

// USD formatter with magnitude-aware precision: above $1 cents
// suffice; under $1 we show 4 decimals so a $0.0042 task doesn't
// read as "$0.00".
function formatRunCost(n: number): string {
  if (!n || n <= 0) return '$0.00';
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.0')}`;
}

export const WorkerDetail: React.FC<Props> = ({ worker, onClose }) => {
  const [stopping, setStopping] = useState(false);

  if (!worker) return null;

  const cancelCurrent = async () => {
    if (!worker.currentTask) return;
    if (!window.confirm(`Остановить «${worker.currentTask.title}»? Процесс получит SIGTERM, незакоммиченная работа потеряется.`)) return;
    setStopping(true);
    try {
      const r = await fetch(`${API}/api/tasks/${worker.currentTask.id}/cancel`, { method: 'POST' });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        alert(`Не удалось остановить: ${data.message || data.error || `HTTP ${r.status}`}`);
      }
      // Live state arrives via WebSocket — no manual refresh needed.
    } catch (err: any) {
      alert(`Сетевая ошибка: ${err.message || 'unknown'}`);
    } finally {
      setStopping(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="worker-detail"
        initial={{ x: 300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 300, opacity: 0 }}
        transition={{ type: 'spring', damping: 25 }}
      >
        <div className="detail-header">
          <div>
            <h2>{worker.avatar} {worker.name}</h2>
            <p className="detail-role">{worker.description}</p>
          </div>
          <button className="detail-close" onClick={onClose}>x</button>
        </div>

        <div className="detail-meta">
          <div className="meta-item">
            <span className="meta-label">Engine</span>
            <span className="meta-value">{worker.engine}</span>
          </div>
          {worker.model && (
            <div className="meta-item">
              <span className="meta-label">Model</span>
              <span className="meta-value">{worker.model}</span>
            </div>
          )}
          <div className="meta-item">
            <span className="meta-label">Uptime</span>
            <span className="meta-value">{worker.uptime}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Tasks</span>
            <span className="meta-value">{worker.completedTasks}/{worker.totalTasks || '∞'}</span>
          </div>
        </div>

        {worker.currentTask && (
          <div className="detail-current-task">
            <div className="detail-task-head">
              <h3>Текущая задача</h3>
              {(worker.status === 'working' || worker.currentTask.status === 'in_progress') && (
                <button
                  className="task-stop-btn"
                  onClick={cancelCurrent}
                  disabled={stopping}
                  title="Послать SIGTERM процессу claude и пометить задачу как failed"
                >
                  {stopping ? '…' : '✕ Остановить'}
                </button>
              )}
            </div>
            <div className="task-card">
              <div className="task-title">{worker.currentTask.title}</div>
              <div className="task-desc">{worker.currentTask.description}</div>
              <div className="task-module">{worker.currentTask.module}</div>
              <div className="worker-progress-bar" style={{ marginTop: 8 }}>
                <motion.div
                  className="worker-progress-fill"
                  animate={{ width: `${worker.currentTask.progress}%` }}
                  style={{ background: '#22c55e' }}
                />
              </div>

              {/* Live token + cost meter — fed by 'task:tokens' WS
                  events from claude-runner. Shown only when we
                  actually have numbers; suppressed for brand-new
                  tasks that haven't generated their first usage
                  event yet. */}
              {worker.currentTask.inputTokens !== undefined && (
                <div className="task-cost-meter">
                  <div className="task-cost-row">
                    <span className="task-cost-label">Input</span>
                    <span className="task-cost-val">{(worker.currentTask.inputTokens ?? 0).toLocaleString('ru-RU')}</span>
                  </div>
                  <div className="task-cost-row">
                    <span className="task-cost-label">Output</span>
                    <span className="task-cost-val">{(worker.currentTask.outputTokens ?? 0).toLocaleString('ru-RU')}</span>
                  </div>
                  <div className="task-cost-row task-cost-total">
                    <span className="task-cost-label">≈ Расход</span>
                    <span className="task-cost-val task-cost-amount">
                      {formatRunCost(worker.currentTask.costUsd ?? 0)}
                    </span>
                  </div>
                  {worker.currentTask.model && (
                    <div className="task-cost-model">{worker.currentTask.model}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="detail-logs">
          <h3>Логи</h3>
          <div className="logs-container">
            {worker.currentTask?.logs.map((log) => (
              <motion.div
                key={log.id}
                className={`log-entry log-${log.type}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <span className="log-icon">{typeIcons[log.type]}</span>
                <span className="log-time">{log.timestamp}</span>
                <span className="log-msg">{log.message}</span>
              </motion.div>
            ))}
            {!worker.currentTask?.logs.length && (
              <div className="log-empty">Нет активных логов</div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
